/**
 * MeasurementOverlay - Issue #101
 *
 * Displays distance measurement when user drags on chart in measure mode.
 * Shows: dotted line, bars count, price variation (points and percentage)
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import GenericChartComponent from "@chartLib/lib/GenericChartComponent";
import { getMouseCanvas } from "@chartLib/lib/GenericComponent";
import { useThemeStore } from "../../../store/themeStore";
import { isExtraDark } from "../../../config/themes";

class MeasurementOverlay extends Component {
  constructor(props) {
    super(props);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
  }

  drawOnCanvas(ctx, moreProps) {
    const { startPoint, endPoint, enabled, formatPrice } = this.props;

    if (!enabled || !startPoint || !endPoint) return;

    const {
      chartConfig: { yScale },
      xScale,
      xAccessor,
      plotData,
    } = moreProps;

    // Get pixel coordinates
    const x1 = startPoint.x;
    const y1 = startPoint.y;
    const x2 = endPoint.x;
    const y2 = endPoint.y;

    // Calculate data values
    const startPrice = yScale.invert(y1);
    const endPrice = yScale.invert(y2);
    const priceChange = endPrice - startPrice;
    const percentChange = (priceChange / startPrice) * 100;

    // Calculate bar count
    const startBarIndex = Math.round(xScale.invert(x1));
    const endBarIndex = Math.round(xScale.invert(x2));
    const barCount = Math.abs(endBarIndex - startBarIndex);

    // Draw dotted line
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([5, 5]);
    ctx.strokeStyle = "#2196F3";
    ctx.lineWidth = 2;
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw start point circle
    ctx.beginPath();
    ctx.arc(x1, y1, 5, 0, 2 * Math.PI);
    ctx.fillStyle = "#2196F3";
    ctx.fill();

    // Draw end point circle
    ctx.beginPath();
    ctx.arc(x2, y2, 5, 0, 2 * Math.PI);
    ctx.fillStyle = "#2196F3";
    ctx.fill();

    // Draw info box
    this.drawInfoBox(
      ctx,
      x2,
      y2,
      barCount,
      priceChange,
      percentChange,
      formatPrice,
    );

    ctx.restore();
  }

  drawInfoBox(ctx, x, y, barCount, priceChange, percentChange, formatPrice) {
    const padding = 12;
    const lineHeight = 22;
    const headerHeight = 28;
    const boxWidth = 180;
    const boxHeight = headerHeight + lineHeight * 3 + padding;

    // Position box to the right of cursor, or left if too close to edge
    const { width } = this.context?.moreProps?.chartConfig || { width: 800 };
    let boxX = x + 15;
    let boxY = y - boxHeight / 2;

    // Adjust if box would go off screen
    if (boxX + boxWidth > width - 10) {
      boxX = x - boxWidth - 15;
    }
    if (boxY < 10) {
      boxY = 10;
    }

    // Format values
    const formattedPrice = formatPrice
      ? formatPrice(Math.abs(priceChange))
      : Math.abs(priceChange).toFixed(2);
    const formattedPercent = Math.abs(percentChange).toFixed(2);
    const priceSign = priceChange >= 0 ? "+" : "-";
    const percentSign = percentChange >= 0 ? "+" : "-";

    // Box background with rounded corners (navy on Extra Dark, slate otherwise)
    ctx.fillStyle = isExtraDark(useThemeStore.getState().theme)
      ? "rgba(9, 14, 25, 0.95)"
      : "rgba(31, 41, 55, 0.95)";
    ctx.strokeStyle = "#444444";
    ctx.lineWidth = 1;

    this.roundRect(ctx, boxX, boxY, boxWidth, boxHeight, 6);
    ctx.fill();
    ctx.stroke();

    // Header background
    ctx.fillStyle = "rgba(55, 65, 81, 0.9)";
    this.roundRectTop(ctx, boxX, boxY, boxWidth, headerHeight, 6);
    ctx.fill();

    // Header text
    ctx.fillStyle = "#ffffff";
    ctx.font =
      "bold 13px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText("📏 Distância", boxX + padding, boxY + headerHeight / 2);

    // Content
    ctx.font =
      "12px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    const contentY = boxY + headerHeight + padding / 2;

    // Row 1: Barras
    ctx.fillStyle = "#9CA3AF";
    ctx.textAlign = "left";
    ctx.fillText("Barras", boxX + padding, contentY + lineHeight * 0.5);
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "right";
    ctx.fillText(
      barCount.toString(),
      boxX + boxWidth - padding,
      contentY + lineHeight * 0.5,
    );

    // Row 2: Variação (points)
    ctx.fillStyle = "#9CA3AF";
    ctx.textAlign = "left";
    ctx.fillText("Variação", boxX + padding, contentY + lineHeight * 1.5);
    ctx.fillStyle = priceChange >= 0 ? "#4ADE80" : "#F87171";
    ctx.textAlign = "right";
    ctx.fillText(
      `${priceSign}${formattedPrice}`,
      boxX + boxWidth - padding,
      contentY + lineHeight * 1.5,
    );

    // Row 3: Percentage
    ctx.fillStyle = "#9CA3AF";
    ctx.textAlign = "left";
    ctx.fillText("Percentual", boxX + padding, contentY + lineHeight * 2.5);
    ctx.fillStyle = percentChange >= 0 ? "#4ADE80" : "#F87171";
    ctx.textAlign = "right";
    ctx.fillText(
      `${percentSign}${formattedPercent}%`,
      boxX + boxWidth - padding,
      contentY + lineHeight * 2.5,
    );
  }

  roundRect(ctx, x, y, width, height, radius) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  roundRectTop(ctx, x, y, width, height, radius) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height);
    ctx.lineTo(x, y + height);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  render() {
    const { enabled } = this.props;

    if (!enabled) return null;

    return (
      <GenericChartComponent
        clip={false}
        canvasDraw={this.drawOnCanvas}
        canvasToDraw={getMouseCanvas}
        drawOn={["mousemove", "pan", "drag"]}
      />
    );
  }
}

MeasurementOverlay.propTypes = {
  enabled: PropTypes.bool,
  startPoint: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  endPoint: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  formatPrice: PropTypes.func,
};

MeasurementOverlay.defaultProps = {
  enabled: false,
  startPoint: null,
  endPoint: null,
  formatPrice: null,
};

export default MeasurementOverlay;
